import PageLayout from "@/pages/settings/components/PageLayout";
import { SettingItemLayoutProps } from "@/pages/settings/components/settingsItems/SettingItemLayout";
import { Month, RevenueModel } from "@/pages/settings/types";
import useAccount, {
  useUpdateAccountDataByKey
} from "@/entities/accounts/hooks";
import { getCurrencySymbol } from "@/pages/deals/helpers";
import {
  useAccountSettings,
  useCurrencies,
  useUpdateAccountSettingByKey
} from "@/entities/settings/hooks";
import { AccountSetting } from "@/entities/settings/types";
import { parseFiscalYearStartMonth } from "@/entities/settings/fiscalYear";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { getEnumTKey, mapEnum } from "@/utils/strings";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import OrgContextDrawer from "@/pages/settings/components/OrgContextDrawer";
import OrgContextItem from "@/pages/settings/components/OrgContextItem";
import {
  ORG_CONTEXT_FIELDS,
  OrgContextValues
} from "@/pages/settings/components/orgContext";
import { SETTINGS_CARD_CONTAINER_CLASS } from "@/pages/settings/components/sections/Section";
import { map } from "lodash";

const TRANSLATION_KEY = "settings.general";

export function extractKeyValuePairs(
  configArray: AccountSetting[]
): Record<string, any> {
  const result: Record<string, any> = {};

  if (!Array.isArray(configArray)) {
    throw new Error("Input must be an array");
  }

  configArray.forEach((item) => {
    if (item && typeof item === "object" && "key" in item && "value" in item) {
      result[item.key] = item.value;
    }
  });

  return result;
}

// The account_settings ICP blob stored under `company_icp`/`person_icp`. It has a fixed
// schema owned by the backend ICP-generate flow — it is NOT a free-form dynamic object.
// The settings UI only ever edits `summary`; `criteria`/`sources`/`confidence` are generated
// server-side and are preserved verbatim on write (the update spreads the existing blob).
interface StoredIcp {
  summary?: string;
  criteria?: unknown;
  sources?: unknown;
  confidence?: unknown;
}

// Parse an account_settings ICP value (already parsed by the API, or still a JSON string)
// into a StoredIcp, or undefined for anything missing/non-object/malformed.
function parseStoredIcp(value: unknown): StoredIcp | undefined {
  let parsed: unknown = value;
  if (typeof value === "string") {
    if (value === "") return undefined;
    try {
      parsed = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  return parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? (parsed as StoredIcp)
    : undefined;
}

// The editable ICP free-text is the `summary` field of the StoredIcp blob — the settings UI
// reads it from account_settings (not the accounts resource, which never carries it).
// Missing/malformed → "".
function parseIcpSummary(value: unknown): string {
  const summary = parseStoredIcp(value)?.summary;
  return typeof summary === "string" ? summary : "";
}

export const GeneralSettings = () => {
  const { t } = useTranslation();

  const accountSettingsQuery = useAccountSettings();
  const accountDataQuery = useAccount();
  const accountSettings = accountSettingsQuery.data;
  const accountData = accountDataQuery.data;
  const [orgName, setOrgName] = useState(accountData?.name || "");
  const [website, setWebsite] = useState(accountData?.website || "");
  const [description, setDescription] = useState(
    accountData?.description || ""
  );
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  // Sourced from account_settings (company_icp/person_icp) in the effect below, not from
  // the accounts resource — see parseIcpSummary.
  const [idealCompanyProfile, setIdealCompanyProfile] = useState("");
  const [idealPersonProfile, setIdealPersonProfile] = useState("");
  const { enabled: showIcp } = useFeatureFlag(FeatureFlags.CRM_5676_ICP);
  const [isOrgContextDrawerOpen, setIsOrgContextDrawerOpen] = useState(false);
  // Which field the drawer should focus when opened. An empty-state CTA sets its own field;
  // the "Edit" button opens the drawer without a focus target.
  const [orgContextFocusField, setOrgContextFocusField] = useState<
    keyof OrgContextValues | undefined
  >();
  const [currency, setCurrency] = useState<string>();
  const [revenueModel, setRevenueModel] = useState<RevenueModel>();
  const months = mapEnum(Month, (value) => value);
  const [selectedMonth, setSelectedMonth] = useState<number>();
  const currencies = useCurrencies().data ?? [];
  const updateSettingsByKey = useUpdateAccountSettingByKey();
  const updateAccountDataByKey = useUpdateAccountDataByKey();

  const isLoadingSettings =
    accountSettingsQuery.isLoading || accountSettingsQuery.isFetching;
  const isLoadingAccount =
    accountDataQuery.isLoading || accountDataQuery.isFetching;
  const isLoading = isLoadingSettings || isLoadingAccount;

  useEffect(() => {
    if (accountSettings && accountSettings.length > 0) {
      const keyValuePairs = extractKeyValuePairs(accountSettings);

      const initialMonth = parseFiscalYearStartMonth(
        keyValuePairs?.fiscal_year_start
      );
      if (initialMonth !== null) {
        setSelectedMonth(initialMonth);
      }

      if (keyValuePairs?.account_currency) {
        setCurrency(keyValuePairs.account_currency);
      }

      if (keyValuePairs?.is_arr !== undefined) {
        const revenueModelValue = keyValuePairs.is_arr
          ? RevenueModel.ARR
          : RevenueModel.MRR;
        setRevenueModel(revenueModelValue);
      }

      setIdealCompanyProfile(parseIcpSummary(keyValuePairs.company_icp));
      setIdealPersonProfile(parseIcpSummary(keyValuePairs.person_icp));
    }
  }, [accountSettings]);

  // Last server value copied into each input. A refetch (e.g. the description-generation poll
  // below) only overwrites a field the user hasn't edited since, so unsaved typing survives.
  const syncedAccountRef = useRef({ name: orgName, website, description });
  useEffect(() => {
    const synced = syncedAccountRef.current;
    const fields = [
      ["name", accountData?.name, orgName, setOrgName],
      ["website", accountData?.website, website, setWebsite],
      ["description", accountData?.description, description, setDescription]
    ] as const;
    for (const [key, serverValue, localValue, setter] of fields) {
      if (serverValue === undefined || serverValue === synced[key]) continue;
      if (localValue === synced[key]) {
        setter(serverValue);
      }
      synced[key] = serverValue;
    }
  }, [
    accountData?.name,
    accountData?.website,
    accountData?.description,
    orgName,
    website,
    description
  ]);

  // Auto-generate description when website exists but description doesn't
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const shouldGenerateDescription = website && !description;

    if (shouldGenerateDescription) {
      setIsGeneratingDescription(true);

      intervalId = setInterval(() => {
        accountDataQuery.refetch();
      }, 10000);
    } else {
      setIsGeneratingDescription(false);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [website, description, accountDataQuery]);

  useEffect(() => {
    if (description && isGeneratingDescription) {
      setIsGeneratingDescription(false);
    }
  }, [description, isGeneratingDescription]);

  const handleUpdateAccountField = (
    fieldKey: string,
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    updateAccountDataByKey.mutate(
      {
        data: {
          [fieldKey]: value
        }
      },
      {
        onSuccess: () => {
          setter(value);
        }
      }
    );
  };

  // ICP free-text is the `.summary` inside the account_settings StoredIcp blob
  // (company_icp/person_icp). Write it via the same settings API as the other settings,
  // merging into the existing blob so generated criteria/sources aren't clobbered.
  const handleUpdateIcpSummary = (
    settingKey: "company_icp" | "person_icp",
    newSummary: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ): Promise<void> => {
    // Never write before settings have loaded: an empty blob here would overwrite the
    // stored ICP and drop its generated criteria/sources. Reject so the caller surfaces it.
    if (!accountSettingsQuery.isSuccess) {
      return Promise.reject(new Error("Account settings are still loading"));
    }
    const existing = extractKeyValuePairs(accountSettings ?? [])[settingKey];
    // Preserve the backend-generated fields (criteria/sources/confidence); only overwrite
    // the editable summary.
    const blob: StoredIcp = parseStoredIcp(existing) ?? {};
    return updateSettingsByKey
      .mutateAsync({
        pathParams: { settingKey },
        data: { value: JSON.stringify({ ...blob, summary: newSummary }) }
      })
      .then(() => {
        setter(newSummary);
      });
  };

  const handleUpdateIsCompleted = (
    fieldKey: string,
    value: any,
    setter: React.Dispatch<React.SetStateAction<any>>,
    onError?: () => void
  ) => {
    let configData = value;
    if (fieldKey === "is_arr") {
      configData = value === RevenueModel.ARR ? true : false;
    }
    if (fieldKey === "fiscal_year_start") {
      // Use Date.UTC to avoid timezone issues - always get the 1st of the selected month in 1970
      const formattedDate = new Date(Date.UTC(1970, value, 1)).toISOString();
      configData = formattedDate;
    }

    // mutateAsync, not mutate: mutate's per-call callbacks only fire for the latest call on this
    // shared mutation, so a currency/month save started meanwhile would drop this one's onError.
    updateSettingsByKey
      .mutateAsync({
        pathParams: { settingKey: fieldKey },
        data: {
          value: configData
        }
      })
      .then(
        () => {
          setter(value);
          configData = "";
        },
        () => onError?.()
      );
  };

  const tabsProps = {
    options: {
      [RevenueModel.MRR]: t(`terms.mrr`),
      [RevenueModel.ARR]: t(`terms.arr`)
    },
    defaultValue: RevenueModel.ARR
  };

  const descriptionItem: SettingItemLayoutProps = {
    title: t(`${TRANSLATION_KEY}.description`),
    actionType: "textarea",
    actionProps: {
      value: description,
      placeholder: isGeneratingDescription
        ? t(`${TRANSLATION_KEY}.descriptionGeneratePlaceholder`)
        : t(`${TRANSLATION_KEY}.descriptionPlaceholder`),
      disabled: isGeneratingDescription,
      onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) =>
        handleUpdateAccountField("description", e.target.value, setDescription),
      onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) =>
        setDescription(e.target.value)
    }
  };

  const openOrgContextDrawer = (focusField?: keyof OrgContextValues) => {
    setOrgContextFocusField(focusField);
    setIsOrgContextDrawerOpen(true);
  };

  // The free-text value for each org-context field, keyed by the OrgContextValues key.
  const orgContextValues: OrgContextValues = {
    description,
    idealCompanyProfile,
    idealPersonProfile
  };

  // Only offer the empty state once the field's backing data has loaded — description lives on
  // the accounts resource, the ICP profiles on account settings. Description also stays quiet
  // while it is being auto-generated, so we don't prompt a manual add mid-generation. Until
  // then OrgContextItem renders the quiet read-only row, avoiding a CTA flash during load.
  const orgContextReady: Record<keyof OrgContextValues, boolean> = {
    description: accountDataQuery.isSuccess && !isGeneratingDescription,
    idealCompanyProfile: accountSettingsQuery.isSuccess,
    idealPersonProfile: accountSettingsQuery.isSuccess
  };

  // Each row is read-only when its value is set (or not yet loaded), and an empty state (with a
  // CTA that opens the drawer focused on that field) when it is loaded and unset. Order and
  // key→i18n-namespace mapping come from the shared ORG_CONTEXT_FIELDS list.
  const orgContextList = (
    <div className={SETTINGS_CARD_CONTAINER_CLASS}>
      {ORG_CONTEXT_FIELDS.map(({ key, namespace }, index) => (
        <OrgContextItem
          key={key}
          data-test={`org-context-${namespace}`}
          title={t(`${TRANSLATION_KEY}.orgContext.${namespace}.title`)}
          value={orgContextValues[key]}
          showEmptyState={orgContextReady[key]}
          notSetLabel={t(`${TRANSLATION_KEY}.orgContext.notSetUp`)}
          emptyHint={t(`${TRANSLATION_KEY}.orgContext.${namespace}.emptyHint`)}
          ctaLabel={t(`${TRANSLATION_KEY}.orgContext.${namespace}.addCta`)}
          onAdd={() => openOrgContextDrawer(key)}
          isLastItem={index === ORG_CONTEXT_FIELDS.length - 1}
        />
      ))}
    </div>
  );

  const orgContextEditAction = showIcp ? (
    <Button
      data-test="edit-org-context"
      variant="tertiary"
      onClick={() => openOrgContextDrawer()}
    >
      {t("edit")}
    </Button>
  ) : undefined;

  if (isLoading && !accountSettings && !accountData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Spinner size="lg" />
        <p className="text-sm text-muted-foreground">
          {t(`${TRANSLATION_KEY}.loading`, "Loading settings...")}
        </p>
      </div>
    );
  }

  return (
    <>
      <PageLayout
        data-test="settings-general-page"
        title={t(`${TRANSLATION_KEY}.title`)}
        subTitle={t(`${TRANSLATION_KEY}.subtitle`)}
        sections={[
          {
            items: [
              {
                title: t(`${TRANSLATION_KEY}.orgName`),
                actionType: "input",
                actionProps: {
                  value: orgName,
                  onBlur: (e: React.ChangeEvent<HTMLInputElement>) =>
                    handleUpdateAccountField(
                      "name",
                      e.target.value,
                      setOrgName
                    ),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    setOrgName(e.target.value)
                }
              },
              {
                title: t(`${TRANSLATION_KEY}.website`),
                actionType: "input",
                actionProps: {
                  value: website,
                  placeholder: t(`${TRANSLATION_KEY}.websitePlaceholder`),
                  onBlur: (e: React.ChangeEvent<HTMLInputElement>) =>
                    handleUpdateAccountField(
                      "website",
                      e.target.value,
                      setWebsite
                    ),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    setWebsite(e.target.value)
                }
              },
              ...(showIcp ? [] : [descriptionItem])
            ]
          },
          ...(showIcp
            ? [
                {
                  title: t(`${TRANSLATION_KEY}.orgContext.title`),
                  subTitle: t(`${TRANSLATION_KEY}.orgContext.subtitle`),
                  action: orgContextEditAction,
                  children: orgContextList
                }
              ]
            : []),
          {
            title: t(`${TRANSLATION_KEY}.financials`),
            items: [
              {
                title: t(`${TRANSLATION_KEY}.currency`),
                subTitle: t(`${TRANSLATION_KEY}.currencySubtitle`),
                actionType: "singleSelect",
                actionProps: {
                  fieldName: "currency",
                  options: map(currencies, (currencyCode) => ({
                    id: currencyCode,
                    displayName: `${getCurrencySymbol(currencyCode)} ${currencyCode}`
                  })),
                  selectedValue: currency,
                  onSelection: (value) =>
                    handleUpdateIsCompleted(
                      "account_currency",
                      value,
                      setCurrency
                    ),
                  formatter: (value: string) => {
                    return `${getCurrencySymbol(value)} ${value}`;
                  }
                }
              },
              {
                title: t(`${TRANSLATION_KEY}.revenue`),
                subTitle: t(`${TRANSLATION_KEY}.revenueSubtitle`),
                actionType: "toggle",
                actionProps: {
                  ...tabsProps,
                  value: revenueModel,
                  // Tabs re-fires on a re-click and on focus after mousedown; skip those so one click sends one save.
                  valueSelected: (value) => {
                    if (value === (revenueModel ?? RevenueModel.ARR)) return;
                    const previous = revenueModel;
                    setRevenueModel(value as RevenueModel);
                    handleUpdateIsCompleted(
                      "is_arr",
                      value,
                      setRevenueModel,
                      () => setRevenueModel(previous)
                    );
                  }
                }
              },
              {
                title: t(`${TRANSLATION_KEY}.firstMonth`),
                actionType: "singleSelect",
                actionProps: {
                  fieldName: "month",
                  options: map(months, (value) => ({
                    id: value,
                    displayName: t(
                      getEnumTKey("settings", "months", Month, Number(value))
                    )
                  })),
                  selectedValue: selectedMonth,
                  onSelection: (value) =>
                    handleUpdateIsCompleted(
                      "fiscal_year_start",
                      value,
                      setSelectedMonth
                    )
                }
              }
            ] as SettingItemLayoutProps[]
          }
        ]}
      />
      <OrgContextDrawer
        isOpen={isOrgContextDrawerOpen}
        onClose={() => setIsOrgContextDrawerOpen(false)}
        focusField={orgContextFocusField}
        values={{ description, idealCompanyProfile, idealPersonProfile }}
        onSaved={async (next) => {
          // description stays on the accounts resource; the ICP free-text is persisted to
          // account_settings (company_icp/person_icp), same as the inline fields. Await all
          // three so the drawer only closes once every write actually succeeds.
          await Promise.all([
            updateAccountDataByKey
              .mutateAsync({ data: { description: next.description } })
              .then(() => setDescription(next.description)),
            handleUpdateIcpSummary(
              "company_icp",
              next.idealCompanyProfile,
              setIdealCompanyProfile
            ),
            handleUpdateIcpSummary(
              "person_icp",
              next.idealPersonProfile,
              setIdealPersonProfile
            )
          ]);
        }}
      />
    </>
  );
};
